import { QueryClient } from '@tanstack/react-query';
import { DEFAULT_STALE_TIME, DEFAULT_GC_TIME } from '@/lib/queryCacheConfig';
import { registerQueryClient } from '@/lib/queryClientHolder';
import { notifyNetworkFailure } from '@/lib/onlineStatus';

const isAuthError = (error: unknown) => {
  const errorKeywords = ['JWT', 'auth', 'Session', 'expired', 'invalid', 'Invalid JWT'];
  const message = error instanceof Error ? error.message : String(error);
  const code = (error as { code?: string })?.code;
  return (
    errorKeywords.some((keyword) => message?.includes(keyword)) ||
    code === 'PGRST301' ||
    code === 'PGRST302'
  );
};

const handleQueryRetry = (failureCount: number, error: unknown) => {
  if (isAuthError(error)) return false;
  return failureCount < 2;
};

const isNetworkError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  const message = error.message;
  return (
    message.includes('Failed to fetch') ||
    message.includes('Network request failed') ||
    message.includes('Load failed')
  );
};

const handleMutationRetry = (failureCount: number, error: unknown) => {
  if (isAuthError(error)) return false;
  if (!isNetworkError(error)) return false;
  // Link is up but WAN is dead (typical Android tablet on LAN Wi-Fi). Flip
  // Query offline so the retry pauses and queues instead of rolling back the
  // optimistic tab/sale. The offline-only probe flips us back when WAN returns.
  notifyNetworkFailure();
  return failureCount < 1;
};

/** Shared QueryClient for use in React (QueryClientProvider) and outside React (organization bridge) */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: handleQueryRetry,
      retryDelay: 1000,
      staleTime: DEFAULT_STALE_TIME,
      gcTime: DEFAULT_GC_TIME,
      // Refetches after the app loses connection to the internet and reconnects:
      refetchOnReconnect: true,
    },
    mutations: {
      retry: handleMutationRetry,
      retryDelay: 1000,
    },
  },
});

// Expose the singleton to non-React callers (auth actions, offline resume, etc.).
registerQueryClient(queryClient);

// NOTE: offline mutation defaults are registered in App.tsx, not here.
// offlineMutations' import graph (services, stores) leads back to this module;
// registering at this module's top level creates an init-order crash.
